import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { formatPriceFR, formatSurfaceFR, formatPhoneFR, formatDateFR } from '../utils/formatFR';
import { getMainEmail, getMainPhone } from '../utils/contactProfile';

const TX_BADGES = {
  cession: ['Cession', 'bg-teal-50', 'text-teal-700'],
  achat: ['Achat', 'bg-emerald-50', 'text-emerald-700'],
  vente_immo: ['Vente', 'bg-blue-50', 'text-blue-700'],
  location: ['Location', 'bg-indigo-50', 'text-indigo-700'],
  location_droit_entree: ['Location + pas-de-porte', 'bg-indigo-50', 'text-indigo-700'],
  location_gerance: ['Location-gérance', 'bg-indigo-50', 'text-indigo-700'],
};

const SoftBadge = ({ label, bg = 'bg-slate-100', fg = 'text-slate-700', icon }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${bg} ${fg}`}>
    {icon && <i className={`bi ${icon}`}></i>}{label}
  </span>
);

const KV = ({ items, cols = 3 }) => {
  const filtered = items.filter(([, v]) => v != null && v !== '' && v !== 'N/C');
  if (!filtered.length) return null;
  return (
    <dl className={`grid gap-x-6 gap-y-2`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {filtered.map(([k, v], i) => (
        <div key={i} className="avoid-break">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{k}</dt>
          <dd className="text-[13px] font-medium text-slate-800 num">{v}</dd>
        </div>
      ))}
    </dl>
  );
};

const Section = ({ icon, title, children }) => (
  <section className="section avoid-break mt-6">
    <div className="flex items-center gap-2 pb-1.5 mb-3 border-b border-slate-200">
      <i className={`bi ${icon} text-[13px]`} style={{ color: 'var(--brand)' }}></i>
      <h2 className="font-title text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{title}</h2>
    </div>
    {children}
  </section>
);

const dedupAddress = (c) => {
  const seen = new Set();
  const parts = [];
  [c.adresse, c.code_postal, c.ville, c.pays].forEach((p) => {
    if (!p) return;
    const key = String(p).trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    parts.push(String(p).trim());
  });
  return parts.join(' ');
};

const range = (min, max, fmt) => {
  if (min && max) return `${fmt(min)} à ${fmt(max)}`;
  if (max) return `Jusqu'à ${fmt(max)}`;
  if (min) return `À partir de ${fmt(min)}`;
  return null;
};

const buildHistory = (c) => {
  const items = [];
  (c.historique_communications || []).forEach((h) => {
    if (!h || typeof h !== 'object') return;
    const typ = (h.type || 'email').toLowerCase();
    const color = typ.includes('mail') ? 'var(--brand)' : (typ.includes('appel') || typ.includes('call') || typ.includes('tel')) ? '#28A745' : '#94A3B8';
    const icon = typ.includes('mail') ? 'bi-envelope-fill' : (typ.includes('appel') || typ.includes('tel')) ? 'bi-telephone-fill' : 'bi-chat-left-text-fill';
    items.push({ date: h.date, label: `${typ.charAt(0).toUpperCase() + typ.slice(1)} — ${h.subject || h.sujet || 'Communication'}`, detail: h.details || h.body || '', color, icon });
  });
  (c.historique_activites || []).forEach((a) => {
    if (!a || typeof a !== 'object') return;
    items.push({ date: a.date, label: String(a.type || 'Activité').charAt(0).toUpperCase() + String(a.type || 'Activité').slice(1), detail: a.details || '', color: '#94A3B8', icon: 'bi-pencil-fill' });
  });
  items.sort((x, y) => new Date(y.date || 0) - new Date(x.date || 0));
  return items.slice(0, 15);
};

const ContactFichePrint = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [media, setMedia] = useState({}); // url d'origine -> object URL authentifié

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contacts/${id}/fiche-data`);
        setData(data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Contact introuvable' : 'Erreur de chargement');
      }
    })();
  }, [id]);

  // Les médias servis via /api/media/* nécessitent le token → on les charge en blob authentifié
  useEffect(() => {
    if (!data) return;
    const urls = [data.agency?.logo_url, data.contact?.photo_url].filter((u) => u && u.startsWith('/api/'));
    let revoked = [];
    (async () => {
      const map = {};
      for (const u of urls) {
        try {
          const res = await api.get(u.replace(/^\/api/, ''), { responseType: 'blob' });
          const obj = window.URL.createObjectURL(res.data);
          map[u] = obj;
          revoked.push(obj);
        } catch (e) { /* ignore */ }
      }
      if (Object.keys(map).length) setMedia((m) => ({ ...m, ...map }));
    })();
    return () => revoked.forEach((o) => window.URL.revokeObjectURL(o));
  }, [data]);

  const resolveMedia = (u) => (u && media[u]) || u;

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/contacts/${id}/fiche-contact?version=agent`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const c = data?.contact || {};
      const safe = `${c.prenom || ''}_${c.nom || ''}`.trim().replace(/[^a-zA-Z0-9À-ÿ_-]+/g, '_').replace(/^_+|_+$/g, '') || id.slice(0, 8);
      a.download = `fiche_contact_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Erreur lors de la génération du PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="fiche-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><i className="bi bi-arrow-clockwise animate-spin text-2xl"></i></div>;

  const c = data.contact;
  const ag = data.agency || {};
  const brand = ag.primary_color || '#007BFF';
  const ic = c.informations_confidentielles || {};
  const cr = c.criteres_recherche || {};
  const prenom = c.prenom || '';
  const nom = c.nom || '';
  const initials = ((prenom[0] || '') + (nom[0] || '')).toUpperCase() || '?';
  const fullname = [c.titre, prenom, nom].filter(Boolean).join(' ').trim() || 'Contact';
  const groupes = (c.groupes || []).slice(0, 5);

  const recherches = (c.recherches && c.recherches.length ? c.recherches : (cr && Object.keys(cr).length ? [cr] : []));
  const financier = [
    ['Revenus annuels', ic.revenus_annuels ? formatPriceFR(ic.revenus_annuels) : null],
    ['Apport disponible', ic.apport_disponible ? formatPriceFR(ic.apport_disponible) : null],
    ["Capacité d'emprunt", ic.capacite_emprunt ? formatPriceFR(ic.capacite_emprunt) : null],
    ['Budget max', cr.budget_max ? formatPriceFR(cr.budget_max) : null],
    ['Score lead', c.score_lead != null ? `${c.score_lead}/100` : null],
    ['Situation familiale', ic.situation_familiale],
    ['Banque', ic.banque],
    ['Notes confidentielles', ic.notes_confidentielles],
  ].filter(([, v]) => v != null && v !== '');
  const history = buildHistory(c);
  const owned = data.owned_properties || [];
  const statusMap = { active: 'En ligne', draft: 'Brouillon', sold: 'Vendu', archived: 'Archivé' };

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC', '--brand': brand }} data-testid="contact-fiche-print">
      <style>{`
        .num { font-variant-numeric: tabular-nums; }
        .font-title { font-family: 'Outfit','Inter',sans-serif; }
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .sheet { box-shadow: none !important; margin: 0 !important; width: auto !important; border-radius: 0 !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .avoid-break, .section, dl, table, tr { page-break-inside: avoid; break-inside: avoid; }
          .page-2 { break-before: page; page-break-before: always; }
        }
      `}</style>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <i className="bi bi-file-earmark-person" style={{ color: brand }}></i>Fiche contact — {fullname}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="fiche-print-btn"
              className="h-9 px-3 rounded-lg text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition">
              <i className="bi bi-printer"></i>Imprimer
            </button>
            <button onClick={downloadPDF} disabled={downloading} data-testid="fiche-pdf-btn"
              className="h-9 px-4 rounded-lg text-sm font-medium text-white shadow-sm flex items-center gap-2 transition disabled:opacity-60" style={{ background: brand }}>
              <i className={`bi ${downloading ? 'bi-hourglass-split animate-spin' : 'bi-download'}`}></i>Télécharger PDF
            </button>
          </div>
        </div>
      </div>

      {/* Sheet */}
      <div className="sheet bg-white mx-auto my-6 shadow-[0_10px_40px_rgba(15,23,42,0.12)] rounded" style={{ width: '210mm', maxWidth: '100%' }}>
        <div className="p-8">
          {/* Header co-branding */}
          <header className="avoid-break flex items-start justify-between gap-6 pb-4 border-b-2 border-slate-100">
            <div className="flex items-center gap-3">
              {ag.logo_url ? (
                <img src={resolveMedia(ag.logo_url)} alt={ag.name || 'Agence'} style={{ maxHeight: '58px', maxWidth: '190px' }} className="object-contain" />
              ) : (
                <>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-title font-bold" style={{ background: brand }}>
                    {(ag.name || 'AG').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="font-title font-bold text-[15px] text-slate-900">{ag.name || 'Agence'}</div>
                </>
              )}
            </div>
            <div className="text-right">
              <div className="font-title font-semibold text-[11px] tracking-[0.16em] uppercase" style={{ color: brand }}>Fiche Contact</div>
              <div className="text-[10.5px] text-slate-400 mt-1 num">Éditée le {formatDateFR(new Date().toISOString())}</div>
              <div className="text-[10.5px] text-slate-400 num">Réf. {c.reference || (c.id || '').slice(0, 12)}</div>
            </div>
          </header>

          {/* Identity banner */}
          <section className="avoid-break flex items-center gap-4 py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-title font-bold text-xl shrink-0 overflow-hidden" style={{ background: brand }}>
              {c.photo_url ? <img src={resolveMedia(c.photo_url)} alt={fullname} className="w-full h-full object-cover" /> : initials}
            </div>
            <div className="min-w-0">
              <h1 className="font-title text-2xl font-bold tracking-tight text-slate-900 leading-tight">{fullname}</h1>
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {c.type_contact && <SoftBadge label={c.type_contact} bg="bg-emerald-50" fg="text-emerald-700" icon="bi-person-check" />}
                {groupes.map((g, i) => <SoftBadge key={i} label={g} icon="bi-tag" />)}
              </div>
            </div>
          </section>

          {/* INFOS */}
          <Section icon="bi-info-circle" title="Infos">
            <KV cols={3} items={[
              ['Type', c.type_contact],
              ['Origine', c.origine],
              ['Statut', c.statut_lead],
              ['Négociateur', data.negociateur_nom],
              ['Langues', (c.langues || []).join(', ')],
              ['Préf. contact', c.preference_communication],
            ]} />
          </Section>

          {/* COORDONNÉES */}
          <Section icon="bi-telephone" title="Coordonnées">
            <KV cols={2} items={[
              ...(c.telephones || []).map((t) => [(t.type || 'Téléphone'), formatPhoneFR(t.numero || t)]),
              ...(c.emails || []).map((e) => [(e.type || 'Email'), e.email || e]),
              c.fax ? ['Fax', c.fax] : ['', ''],
              ['Adresse', dedupAddress(c)],
            ]} />
          </Section>

          {/* SOCIÉTÉ */}
          {(c.nom_societe || c.profession || c.siret) && (
            <Section icon="bi-building" title="Société">
              <KV cols={3} items={[
                ['Société', c.nom_societe],
                ['Fonction / Profession', c.profession],
                ['SIRET', c.siret || ic.siret],
              ]} />
            </Section>
          )}

          {/* PROFIL FINANCIER (amber) */}
          {financier.length > 0 && (
            <section className="section avoid-break mt-6 rounded-xl p-4" style={{ background: '#FFFBEB', border: '1px solid #FCD34D' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-title font-semibold text-[11px] tracking-[0.12em] uppercase text-amber-900 flex items-center gap-2">
                  <i className="bi bi-shield-lock-fill text-amber-600"></i>Profil financier
                </div>
                <SoftBadge label="Confidentiel" bg="bg-amber-100" fg="text-amber-800" icon="bi-eye-slash" />
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
                {financier.map(([k, v], i) => (
                  <div key={i} className="avoid-break">
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-amber-700/80">{k}</dt>
                    <dd className="text-[13px] font-semibold text-amber-900 num">{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {/* RECHERCHE */}
          {recherches.length > 0 && (
            <Section icon="bi-search" title="Recherche">
              <div className="space-y-4">
                {recherches.map((r, idx) => {
                  const tx = TX_BADGES[r.operation];
                  return (
                    <div key={idx} className="avoid-break">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold text-[13px] text-slate-800">{r.label || r.titre || `Recherche #${idx + 1}`}</div>
                        {tx && <SoftBadge label={tx[0]} bg={tx[1]} fg={tx[2]} icon="bi-cash-coin" />}
                      </div>
                      <KV cols={3} items={[
                        ['Catégorie', r.profil],
                        ['Types de bien', (r.types_biens || []).join(', ')],
                        ['Budget', range(r.budget_min, r.budget_max, formatPriceFR)],
                        ['Surface', range(r.surface_min, r.surface_max, formatSurfaceFR)],
                        ['Loyer max', r.loyer_max ? formatPriceFR(r.loyer_max) : null],
                        ['Villes', (r.villes || []).join(', ')],
                        ['Secteurs', (r.secteurs || []).join(', ')],
                        ['Notes', r.notes],
                      ]} />
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* BIENS EN PORTEFEUILLE */}
          {owned.length > 0 && (
            <Section icon="bi-houses" title="Biens en portefeuille">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                    <th className="py-2 font-semibold w-24">Réf.</th>
                    <th className="py-2 font-semibold">Bien</th>
                    <th className="py-2 font-semibold w-28">Transaction</th>
                    <th className="py-2 font-semibold w-24 text-right">Statut</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  {owned.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="py-2 num font-medium">{p.reference}</td>
                      <td className="py-2">{p.titre}</td>
                      <td className="py-2">{String(p.operation || '').replace(/_/g, ' ')}</td>
                      <td className="py-2 text-right">{statusMap[p.status] || p.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* HISTORIQUE — page 2 */}
          {history.length > 0 && (
            <div className="page-2">
              <Section icon="bi-clock-history" title="Historique">
                <div className="relative pl-6">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200"></div>
                  {history.map((h, i) => (
                    <div key={i} className="relative pb-3.5 avoid-break">
                      <span className="absolute -left-[17px] top-1 w-2.5 h-2.5 rounded-full ring-2 ring-white" style={{ background: h.color }}></span>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="text-[12.5px] font-medium text-slate-800">{h.label}</div>
                        <div className="text-[10.5px] text-slate-400 num shrink-0">{formatDateFR(h.date)}</div>
                      </div>
                      {h.detail && <div className="text-[11px] text-slate-500 mt-0.5">{h.detail}</div>}
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}

          {/* Footer co-branding */}
          <footer className="avoid-break mt-8 pt-4 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-400">
            <span>{ag.name || 'Agence'} · Document professionnel et confidentiel</span>
            <span className="flex items-center gap-1.5">Propulsé par <span className="font-title font-bold text-slate-500">Komerces</span></span>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ContactFichePrint;
